import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button } from '@els/els-react--button';
import { Banner } from '@els/els-react--banner';
import { SortOrder, Table } from '@els/els-react--table';
import { dispatchStoreProducts } from '@Containers/RcsResults/RcsResultsActions';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import isArray from 'lodash/isArray';
import {
  ALL_VISIBLE_SUPPLIERS_COUNT,
  COMMERCIAL_SUPPLIERS_TABLE_CAPTION,
  INITIAL_VISIBLE_SUPPLIER_COUNT,
  OUTER_TABLE_INITIAL_SORT_BY,
} from './constants';
import {
  buildCommercialSupplierTableData,
  buildCommercialSupplierTableDataKey,
  compareCommercialSupplierTableSortValues,
  getCommercialSupplierTableColumns,
  getCommercialSupplierTableSortableColumns,
  groupCommercialSupplierRows,
} from './SubstanceCommercialSupplierInfo.helpers';
import SubstanceCommercialSupplierAggregatorModal from './SubstanceCommercialSupplierAggregatorModal';
import SubstanceCommercialSupplierDetailRow from './SubstanceCommercialSupplierDetailRow';
import SubstanceCommercialSupplierSummaryRow from './SubstanceCommercialSupplierSummaryRow';
import SubstanceCommercialSupplierTableHead from './SubstanceCommercialSupplierTableHead';
import SubstanceCommercialSupplierPriceSortTooltip from './SubstanceCommercialSupplierPriceSortTooltip';

import './SubstanceCommercialSupplierInfo.scss';

function SubstanceCommercialSupplierInformation(props) {
  const {
    data: commercialSupplierData,
    uniqueId,
    metadata = {},
    supplierCount,
    total,
    details = {},
    substanceId,
    substanceIndex,
    isEmbeddedInCard = false,
  } = props;
  const [bannerVisible, setBannerVisible] = useState(true);
  const [expandedSupplierIds, setExpandedSupplierIds] = useState({});
  const [aggregatorModalSupplierId, setAggregatorModalSupplierId] = useState(null);
  const [visibleSupplierCount, setVisibleSupplierCount] = useState(INITIAL_VISIBLE_SUPPLIER_COUNT);
  const [outerSortAnnouncement, setOuterSortAnnouncement] = useState('');
  const id = `${uniqueId}-commercialSuppliers`;
  const isProdSelectionEnabled = Auth.isFeatureEnabled(FEATURES.enableProductSelection);
  const isRcsExport = Auth.getUser()?.details?.databases?.[metadata.databaseId]?.name === 'Commercial Substances';
  const data = useMemo(() => commercialSupplierData || [], [commercialSupplierData]);
  const { casRegistryNumbers } = details;
  const handledCasRegistryNumbers = isArray(casRegistryNumbers) ? casRegistryNumbers.join(', ') : casRegistryNumbers;
  const groupedData = useMemo(() => groupCommercialSupplierRows(data), [data]);
  const tableData = useMemo(() => buildCommercialSupplierTableData(groupedData), [groupedData]);
  const tableDataKey = useMemo(() => buildCommercialSupplierTableDataKey(tableData), [tableData]);
  const tableColumns = useMemo(
    () => getCommercialSupplierTableColumns(isProdSelectionEnabled, tableData),
    [isProdSelectionEnabled, tableData]
  );
  const tableSortableColumns = useMemo(() => getCommercialSupplierTableSortableColumns(tableColumns, tableData), [tableColumns, tableData]);
  const isOuterPriceSortable = Boolean(tableSortableColumns.price);
  const tableColumnsWithPriceSortInfo = useMemo(
    () =>
      tableColumns.map((column) =>
        column.value === 'price' && !isOuterPriceSortable
          ? {
              ...column,
              headContentRight: <SubstanceCommercialSupplierPriceSortTooltip id={`${id}-price-sort-tooltip`} />,
            }
          : column
      ),
    [id, isOuterPriceSortable, tableColumns]
  );
  const tableColumnIds = useMemo(() => tableColumns.map((column) => column.value), [tableColumns]);
  const sortableColumnIds = useMemo(() => Object.keys(tableSortableColumns), [tableSortableColumns]);
  const tableInitialSortBy = tableSortableColumns[OUTER_TABLE_INITIAL_SORT_BY] ? OUTER_TABLE_INITIAL_SORT_BY : sortableColumnIds[0];
  const aggregatorModalGroup = useMemo(
    () => tableData.find((group) => group.supplierId === aggregatorModalSupplierId) || null,
    [aggregatorModalSupplierId, tableData]
  );

  useEffect(() => {
    setExpandedSupplierIds({});
    setAggregatorModalSupplierId(null);
    setVisibleSupplierCount(INITIAL_VISIBLE_SUPPLIER_COUNT);
  }, [tableDataKey, total]);

  useEffect(() => {
    if (isRcsExport && isProdSelectionEnabled) {
      dispatchStoreProducts({ substanceId, data });
    }
  }, [isRcsExport, isProdSelectionEnabled, substanceId, data]);

  useEffect(() => {
    if (aggregatorModalSupplierId && !aggregatorModalGroup) {
      setAggregatorModalSupplierId(null);
    }
  }, [aggregatorModalSupplierId, aggregatorModalGroup]);

  const totalColumns = tableColumns.length;
  const hasMoreVisibleSuppliers = tableData.length > visibleSupplierCount;
  const titleCount = supplierCount ?? tableData.length;

  const toggleSupplierExpansion = (supplierId) => {
    setExpandedSupplierIds((previousExpandedSupplierIds) => ({
      ...previousExpandedSupplierIds,
      [supplierId]: !previousExpandedSupplierIds[supplierId],
    }));
  };

  const handleShowAllSuppliers = () => {
    setVisibleSupplierCount(ALL_VISIBLE_SUPPLIERS_COUNT);
  };

  return (
    <div id={id} className="substance-results-commercial-information u-els-font-size-base u-els-line-height-default">
      <div className={`rcs-commercial-card${isEmbeddedInCard ? ' rcs-commercial-card--embedded' : ''}`}>
        {!isEmbeddedInCard && <div className="rcs-commercial-card__header">{`Commercial Suppliers - ${titleCount}`}</div>}
        <div className="rcs-commercial-card__body">
          <Banner
            id="rcs-price-notify"
            type="info"
            size="small"
            isVisible={bannerVisible}
            onClose={() => setBannerVisible(false)}
            hideCloseLabel>
            Please take a moment to check individual supplier websites for the most up-to-date information on pricing and availability
          </Banner>

          <div className="rx-reflow-scrollable rcs-commercial-table-scroll">
            <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
              {outerSortAnnouncement}
            </span>
            <Table
              a11y={{
                name: 'Commercial Suppliers',
                description: COMMERCIAL_SUPPLIERS_TABLE_CAPTION,
              }}
              allowNeutralSort
              className={`rx-table rx-table--full-width rcs-commercial-table ${
                hasMoreVisibleSuppliers ? '' : 'rcs-commercial-table--without-footer'
              }`}
              columns={tableColumnsWithPriceSortInfo}
              customSortFunction={compareCommercialSupplierTableSortValues}
              data={tableData}
              id={`${id}-table`}
              initialSortBy={tableInitialSortBy}
              initialSortOrder={SortOrder.NEUTRAL}
              isSortable={sortableColumnIds.length > 0}
              key={tableDataKey}
              renderHead={(rowProps, sortingProps) => (
                <SubstanceCommercialSupplierTableHead
                  rowProps={rowProps}
                  sortingProps={sortingProps}
                  onSortAnnouncement={setOuterSortAnnouncement}
                />
              )}
              rowKey="supplierId"
              sortableColumns={tableSortableColumns}
              subtleDividers
              useSemantic
              verticalDividers
              renderDatum={(supplierGroups = []) =>
                supplierGroups?.slice(0, visibleSupplierCount)?.map((group) => {
                  const isExpanded = Boolean(expandedSupplierIds[group.supplierId]);
                  const detailRowId = `${id}-${group.supplierId}`;

                  return (
                    <Fragment key={group.supplierId}>
                      <SubstanceCommercialSupplierSummaryRow
                        group={group}
                        isExpanded={isExpanded}
                        onToggle={() => toggleSupplierExpansion(group.supplierId)}
                        handledCasRegistryNumbers={handledCasRegistryNumbers}
                        databaseId={metadata.databaseId}
                        detailRowId={detailRowId}
                        isProdSelectionEnabled={isProdSelectionEnabled}
                        visibleColumnIds={tableColumnIds}
                      />
                      {isExpanded && (
                        <SubstanceCommercialSupplierDetailRow
                          group={group}
                          casRegistryNumbers={handledCasRegistryNumbers}
                          detailRowId={detailRowId}
                          isProdSelectionEnabled={isProdSelectionEnabled}
                          onOpenAggregatorModal={() => setAggregatorModalSupplierId(group.supplierId)}
                          substanceId={substanceId}
                          substanceIndex={substanceIndex}
                          totalColumns={totalColumns}
                        />
                      )}
                    </Fragment>
                  );
                })
              }
            />
          </div>

          {aggregatorModalGroup && (
            <SubstanceCommercialSupplierAggregatorModal group={aggregatorModalGroup} onClose={() => setAggregatorModalSupplierId(null)} />
          )}

          {hasMoreVisibleSuppliers && (
            <div className="rcs-commercial-card__footer">
              <Button data-e2e="commercial-supplier-show-all-results" type="tertiary" size="small" onClick={handleShowAllSuppliers}>
                Show all
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SubstanceCommercialSupplierInformation;
